
    import Vue from 'vue';
    import Component from 'vue-class-component';
    import '@mdi/font/css/materialdesignicons.css';
    import Preloader from '@/components/Preloader.vue';
    import RawConversionDialog from '@/components/RawConversionDialog.vue';
    import LogoutScreen from '@/components/LogoutScreen.vue';
    import sessionGuard from '@/service/sessionGuard';
    import api, { refreshSession } from '@/service/api';
    import { hasSessionProtection } from '@/service/sessionProtection';
    import { getModule } from 'vuex-module-decorators';
    import UploadModule from '@/store/modules/Upload';

    @Component({
        components: {
            Preloader,
            RawConversionDialog,
            LogoutScreen,
        },
    })
    export default class App extends Vue {
        private backgroundOpacity: number = 1;
        private scrollListener!: (ev: Event) => void;
        private scrollRafId: number | null = null;
        private isScrollListenerAttached = false;
        private isPreloaderActive = true;
        private hasPreloaderCompleted = false;
        private forceGlobalPreloader = false;
        private unregisterRouteGuard: (() => void) | null = null;
        private authPreloaderStartListener!: EventListener;
        private unsubscribeSessionGuard: (() => void) | null = null;

        private get showLogoutScreen(): boolean {
            return this.$store.state.user.loggedOut;
        }

        private mounted() {
            this.unsubscribeSessionGuard = sessionGuard.subscribe(() => {
                this.$store.commit('setLoggedOut', true);
            });

            const uploadModule = getModule(UploadModule, this.$store);
            this.$watch(
                () => this.$store.state.user.loginState === 'authenticated' && !this.showLogoutScreen,
                (authenticated: boolean) => {
                    if (authenticated) {
                        sessionGuard.start(
                            () => uploadModule.uploadInProgress || hasSessionProtection(),
                            () => refreshSession(),
                            () => api.checkSession(),
                        );
                    } else {
                        sessionGuard.stop();
                    }
                },
                { immediate: true },
            );

            // Scroll listener for background opacity
            this.scrollListener = () => {
                if (this.isHomeRoute || this.scrollRafId !== null) {
                    return;
                }

                this.scrollRafId = requestAnimationFrame(() => {
                    this.scrollRafId = null;
                    this.updateBackgroundOpacity();
                });
            };

            this.authPreloaderStartListener = () => {
                this.forceGlobalPreloader = true;
                this.hasPreloaderCompleted = false;
                this.isPreloaderActive = true;
            };
            window.addEventListener('raws-preloader-start', this.authPreloaderStartListener);

            this.unregisterRouteGuard = this.$router.beforeEach((to, from, next) => {
                const fromHome = from.path === '/';
                const toAppRoute = to.path !== '/';
                if (fromHome && toAppRoute) {
                    this.forceGlobalPreloader = true;
                    this.hasPreloaderCompleted = false;
                    this.isPreloaderActive = true;
                }
                next();
            });

            this.syncPreloaderState();
            this.$watch(() => this.$route.fullPath, () => {
                this.syncPreloaderState();
                this.syncScrollTracking();
            });
            this.syncScrollTracking();
        }

        private beforeDestroy() {
            sessionGuard.stop();
            this.detachScrollListener();
            if (this.scrollRafId !== null) {
                cancelAnimationFrame(this.scrollRafId);
                this.scrollRafId = null;
            }
            window.removeEventListener('raws-preloader-start', this.authPreloaderStartListener);
            if (this.unregisterRouteGuard) {
                this.unregisterRouteGuard();
                this.unregisterRouteGuard = null;
            }
            if (this.unsubscribeSessionGuard) {
                this.unsubscribeSessionGuard();
                this.unsubscribeSessionGuard = null;
            }
        }

        private get isHomeRoute(): boolean {
            return this.$route.path === '/' && window.location.pathname === '/';
        }

        private get isSessionsRoute(): boolean {
            return this.$route.path.startsWith('/sessions');
        }

        private get useGlobalPreloader(): boolean {
            return !this.isHomeRoute || this.forceGlobalPreloader;
        }

        private get preloaderDisableIntroBlur(): boolean {
            return this.forceGlobalPreloader;
        }

        private get useGlobalShellBg(): boolean {
            return !this.isHomeRoute;
        }

        private get shouldHideContent(): boolean {
            return this.useGlobalPreloader && this.isPreloaderActive;
        }

        private get mainShellStyle(): string {
            if (this.isHomeRoute) {
                return '';
            }
            return `box-shadow: inset 0 0 0 150vw rgba(020,020,020,${1 - this.backgroundOpacity})`;
        }

        private syncPreloaderState() {
            if (!this.useGlobalPreloader) {
                this.isPreloaderActive = false;
                return;
            }

            // The global preloader is only for entering the app. Route changes inside
            // the app must not hide already rendered content behind an inactive overlay.
            this.isPreloaderActive = !this.hasPreloaderCompleted;
        }

        private updateBackgroundOpacity() {
            const startScroll = 100;
            const endScroll = 200;
            const boundedScroll =
                Math.min(
                    Math.max(window.scrollY, startScroll),
                    endScroll,
                ) - startScroll;
            const scrollRange = endScroll - startScroll;
            const scrollFraction = boundedScroll / scrollRange;
            const nextOpacity = 1 - 0.8 * scrollFraction;

            if (Math.abs(nextOpacity - this.backgroundOpacity) > 0.001) {
                this.backgroundOpacity = nextOpacity;
            }
        }

        private syncScrollTracking() {
            if (this.isHomeRoute) {
                this.detachScrollListener();
                this.backgroundOpacity = 1;
                return;
            }

            if (!this.isScrollListenerAttached) {
                window.addEventListener('scroll', this.scrollListener, { passive: true });
                this.isScrollListenerAttached = true;
            }
            this.updateBackgroundOpacity();
        }

        private detachScrollListener() {
            if (!this.isScrollListenerAttached) {
                return;
            }

            window.removeEventListener('scroll', this.scrollListener);
            this.isScrollListenerAttached = false;
        }

        private onPreloaderFadeStart() {
            // The opaque preloader still covers the page while app content is painted.
            this.isPreloaderActive = false;
        }

        private onPreloaderComplete() {
            this.isPreloaderActive = false;
            this.hasPreloaderCompleted = true;
            this.forceGlobalPreloader = false;
        }

    }
